import * as React from "react";
import { HashRouter as Router, Route, Link, Switch } from "react-router-dom";
import { Provider } from "react-redux";
import { ThemeProvider } from "styled-components";
import { GoogleOAuthProvider } from "@react-oauth/google";

import { GOOGLE_LOGIN_CLIENT_ID, IS_DEV } from "@config/consts";
import { ROUTES } from "@config/soft.routing";

import DefaultTheme from "@ui/themes/default";
import { Container } from "@ui/Atoms/Grid";
import { ErrorBoundary } from "@ui/Atoms";
import { ErrorFallback } from "@ui/Molecules";

import { resolveCountryFromUrl } from "@services/ResolveCountryFromUrl";

import { MakeRedirect } from "@containers/MakeRedirect";

import { BreakpointProvider } from "@hooks/useBreakpoint";
import { useTranslate } from "@hooks/useTranslate";
import { PrismicProvider } from "@hooks/usePrismic";
import { FeatureToggleProvider } from "@hooks/useFeatureToggle";
import { ConfigProvider } from "@hooks/useConfig";
import { ModalProvider } from "@hooks/modal/ModalProvider";

import store from "@soft/redux/store";
import { LanguageDetector } from "@soft/containers/LanguageDetector";
import {
  DefaultLayoutSoft,
  AuthGuard,
  LocalStoragePurger,
  Analytics,
  AgentGuard,
} from "@soft/containers";
import {
  ClientDashboard,
  ValidateContract,
  ValidateContractInvalidLink,
  NotFound,
  Redirection,
  PaymentProcessed,
  Booking,
  AccountCreation,
  DeclareEmployee,
} from "@soft/pages";

const AppComponent = (): React.ReactElement => {
  const translate = useTranslate();

  return (
    <ThemeProvider theme={DefaultTheme}>
      <ErrorBoundary
        fallbackUi={() => (
          <ErrorFallback
            somethingWentWrongText={translate(
              "batsoft_pwa_generic.something_went_wrong",
            )}
            appCrashExplanationText={translate(
              "batsoft_pwa_generic.app_crash_explanation",
            )}
          />
        )}
      >
        <Provider store={store}>
          <BreakpointProvider>
            <PrismicProvider>
              <GoogleOAuthProvider clientId={String(GOOGLE_LOGIN_CLIENT_ID)}>
                <ConfigProvider country={resolveCountryFromUrl()}>
                  <LanguageDetector>
                    <ModalProvider>
                      <FeatureToggleProvider>
                        <Router>
                          <React.Fragment>
                            <LocalStoragePurger />
                            <DefaultLayoutSoft>
                              <Analytics />
                              <Switch>
                                <Route
                                  exact
                                  path={ROUTES.BASE}
                                  render={() => (
                                    <React.Fragment>
                                      <MakeRedirect
                                        possibleRoutes={["client"]}
                                      />
                                      {IS_DEV && (
                                        <Container>
                                          <h1>Batsoft:</h1>
                                          <ul>
                                            <li>
                                              <Link
                                                to={ROUTES.CLIENT_DASHBOARD}
                                              >
                                                Client page
                                              </Link>
                                            </li>
                                            <li>
                                              <Link
                                                to={ROUTES.VALIDATE_CONTRACT}
                                              >
                                                Validate Contract
                                              </Link>
                                            </li>
                                            <li>
                                              <Link
                                                to={
                                                  ROUTES.VALIDATE_CONTRACT_LINK_INVALID
                                                }
                                              >
                                                Validate Contract Invalid Link
                                              </Link>
                                            </li>
                                            <li>
                                              <Link
                                                // check README.MD in pages/Booking/HomeCleaning on how to get correct route params
                                                to={`${ROUTES.BOOKING}?contractUuid=8c3be12c-779d-11eb-833f-9e8b09604348&type=onceJob`}
                                              >
                                                Booking (Single Job/Contract)
                                              </Link>
                                            </li>
                                            <li>
                                              <Link
                                                to={ROUTES.ACCOUNT_CREATION}
                                              >
                                                Account Creation
                                              </Link>
                                            </li>
                                            <li>
                                              <Link
                                                to={ROUTES.DECLARE_EMPLOYEE}
                                              >
                                                Declare Employee
                                              </Link>
                                            </li>
                                          </ul>
                                        </Container>
                                      )}
                                    </React.Fragment>
                                  )}
                                />
                                <Route
                                  path={ROUTES.CLIENT_DASHBOARD}
                                  render={props => (
                                    <AgentGuard>
                                      <AuthGuard redirect={ROUTES.REDIRECTION}>
                                        <ClientDashboard {...(props as any)} />
                                      </AuthGuard>
                                    </AgentGuard>
                                  )}
                                />
                                <Route
                                  path={ROUTES.VALIDATE_CONTRACT}
                                  render={props => (
                                    <AuthGuard redirect={ROUTES.REDIRECTION}>
                                      <ValidateContract {...(props as any)} />
                                    </AuthGuard>
                                  )}
                                />
                                <Route
                                  path={ROUTES.VALIDATE_CONTRACT_LINK_INVALID}
                                  render={props => (
                                    <AuthGuard redirect={ROUTES.REDIRECTION}>
                                      <ValidateContractInvalidLink
                                        {...(props as any)}
                                      />
                                    </AuthGuard>
                                  )}
                                />
                                <Route
                                  path={ROUTES.BOOKING}
                                  render={props => (
                                    <AuthGuard
                                      redirect={ROUTES.REDIRECTION}
                                      asAdminOnly
                                    >
                                      <Booking {...(props as any)} />
                                    </AuthGuard>
                                  )}
                                />
                                <Route
                                  path={ROUTES.ACCOUNT_CREATION}
                                  render={props => (
                                    <AccountCreation {...(props as any)} />
                                  )}
                                />
                                <Route
                                  path={ROUTES.DECLARE_EMPLOYEE}
                                  render={props => (
                                    <AgentGuard>
                                      <AuthGuard redirect={ROUTES.REDIRECTION}>
                                        <DeclareEmployee {...(props as any)} />
                                      </AuthGuard>
                                    </AgentGuard>
                                  )}
                                />
                                <Route
                                  path={ROUTES.NOT_FOUND}
                                  component={NotFound}
                                />
                                <Route
                                  path={ROUTES.REDIRECTION}
                                  component={Redirection}
                                />
                                <Route
                                  path={ROUTES.PAYMENT_PROCESSED}
                                  component={PaymentProcessed}
                                />
                                <Route component={NotFound} />
                              </Switch>
                            </DefaultLayoutSoft>
                          </React.Fragment>
                        </Router>
                      </FeatureToggleProvider>
                    </ModalProvider>
                  </LanguageDetector>
                </ConfigProvider>
              </GoogleOAuthProvider>
            </PrismicProvider>
          </BreakpointProvider>
        </Provider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export { AppComponent as App };
